import { StorageError } from '../../app/errors';
import type { MinuteRederivation, ObservationKind, ObservationRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * **INV-1's repo surface.** *"`Observation` is append-only. The only permitted mutations are
 * deletion of `increment` observations within the undo window (LIVE-6), and boundary-driven
 * `matchMinute` re-derivation (SES-8). No other mutation exists on any code path. An `Observation`
 * of kind `voiceNote` or `flag` is never deleted by any user action other than cascade deletion of
 * its Match or Team (INV-8)."*
 *
 * INV-1 is a universal negative about *all* code paths, so it cannot be discharged by tests — only
 * by there being no other path. Four mechanisms, in order of how hard they are to get around:
 *
 * 1. **This module exports exactly four mutators**, and each is named for the one clause that
 *    permits it: `insertObservation` (append), `deleteIncrement` (LIVE-6), `rederiveMinutes`
 *    (SES-8), `deleteObservationsForCascade` (INV-8). There is no `update`, no `put`, no generic
 *    `delete`. A caller that wants to change a `kind` or an `audioBlobId` has nothing to call.
 * 2. **`deleteIncrement` re-reads its target inside the transaction** and refuses anything that is
 *    not an `increment` on the named hypothesis. This is stronger than a compile-time brand, which
 *    a cast removes: the check runs at the moment of deletion, against the committed row, on the
 *    only path that reaches it.
 * 3. **`deleteIncrement` is selected from the `[matchId+hypothesisId+kind+createdAt]` range with
 *    `kind='increment'` pinned** (design §3.4), so a `voiceNote` or `flag` row is not *in* the
 *    queried range in the first place. Point 2 is the backstop for point 3, not a substitute.
 * 4. **A lint rule (`inv-1/observation-append-only`) refuses any write method on an `observations`
 *    table handle outside this file**, and the NFR-6 boundary refuses `dexie` outside `data/`. So
 *    the table cannot be reached around this module at all.
 *
 * `src/test/boundary.test.ts` proves mechanism 4 fires by feeding it a real violation, and
 * `observations.test.ts` proves 2 and 3 by attempting the forbidden deletions.
 */

const INCREMENT: ObservationKind = 'increment';

/**
 * Appends one Observation.
 *
 * On the tap path this is one of exactly two row writes in the §5.1 transaction, so it does nothing
 * but validate two integers and `add`. `add` rather than `put`: a colliding id is a defect that must
 * surface, not an overwrite of a committed observation, which would be a mutation INV-1 forbids.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, on a `matchMinute` that would
 *   violate INV-10, or on a write failure.
 */
export async function insertObservation(row: ObservationRow): Promise<void> {
  requireTransaction('insertObservation');
  // INV-10: "no reachable state permits an Observation with an undefined or null `matchMinute`."
  // `domain/clock.ts` guarantees it for rows this app derives; this closes the import path, where
  // the row comes from a file and nothing upstream has checked it.
  if (!Number.isInteger(row.matchMinute) || row.matchMinute < 0) {
    throw new StorageError(
      `INV-10: an Observation needs a finite non-negative integer matchMinute; got ${String(row.matchMinute)}.`,
      { context: { store: TABLE.observations, id: row.id, matchMinute: row.matchMinute } },
    );
  }
  try {
    await db().observations.add(row);
  } catch (cause) {
    throw new StorageError('Could not record the observation.', {
      cause,
      context: { store: TABLE.observations, id: row.id, kind: row.kind },
    });
  }
}

/** The one deletion LIVE-6 permits, named so it cannot be mistaken for a general one. */
export interface IncrementDeletion {
  matchId: string;
  hypothesisId: string;
  /** Chosen by `domain/undo.planUndoIncrement` from `listUndoCandidates`. */
  observationId: string;
}

/**
 * Deletes one `increment` Observation — LIVE-6's undo, and nothing else.
 *
 * Re-reads the row inside the caller's transaction and refuses if it is not an `increment`, or not
 * on the named hypothesis, or not in the named match. LIVE-6's three MUST-NOTs — *"MUST NOT delete
 * an increment belonging to a different hypothesis, MUST NOT delete a `voiceNote` or `flag`
 * observation"* — are therefore properties of this function rather than of the query that fed it.
 *
 * @throws {StorageError} outside a transaction, if the row is missing, if it is not a deletable
 *   increment, or on a write failure. The transaction aborts and nothing commits, so SES-2 means no
 *   acknowledgment renders either.
 */
export async function deleteIncrement(target: IncrementDeletion): Promise<void> {
  requireTransaction('deleteIncrement');
  const table = db().observations;
  const row = await table.get(target.observationId);

  if (!row) {
    throw new StorageError(
      `Observation ${target.observationId} no longer exists; nothing was undone.`,
      { context: { store: TABLE.observations, ...target } },
    );
  }
  if (row.kind !== INCREMENT) {
    throw new StorageError(
      `INV-1: observation ${row.id} is a ${row.kind}, which undo may never delete. Only cascade deletion of its Match or Team removes it.`,
      { context: { store: TABLE.observations, id: row.id, kind: row.kind } },
    );
  }
  if (row.hypothesisId !== target.hypothesisId || row.matchId !== target.matchId) {
    throw new StorageError(
      `LIVE-6: observation ${row.id} belongs to a different hypothesis or match; undo may only walk back its own.`,
      {
        context: {
          store: TABLE.observations,
          id: row.id,
          rowHypothesisId: row.hypothesisId,
          rowMatchId: row.matchId,
          requested: target,
        },
      },
    );
  }

  try {
    await table.delete(row.id);
  } catch (cause) {
    throw new StorageError('Could not undo the increment.', {
      cause,
      context: { store: TABLE.observations, id: row.id },
    });
  }
}

/**
 * SES-8 — re-exported, not declared.
 *
 * The shape moved to `types/` when U-14a gave SES-8 a command path: `domain/clock` computes the
 * minutes and a `RowOp` carries them here, and `domain/` may not import `data/` (§1.2). Kept
 * exported from this module because `matchNotes` and every existing caller import it from here, and
 * one declaration with one re-export is a shorter path to "the two halves cannot drift" than two
 * declarations that happen to match.
 */
export type { MinuteRederivation } from '../../types';

/**
 * SES-8 boundary re-derivation: rewrites `matchMinute` and nothing else.
 *
 * The parameter type is the mechanism. It carries an id and a minute, so no other field is *in
 * scope* to be written — a caller cannot accidentally carry a stale `kind` or `createdAt` through a
 * spread, which is exactly how an "update the minute" helper turns into a general mutator.
 *
 * INV-7 is why this exists at all: match-minute values derive from wall-clock anchors, and adjusting
 * an anchor at triage (§10.1) must move every minute derived from it. `createdAt` is untouched —
 * it is metadata, not a record key.
 *
 * @returns how many rows were rewritten, so the caller can report the SES-8 change honestly.
 * @throws {StorageError} outside a transaction, on a missing row, on a minute that would violate
 *   INV-10, or on a write failure.
 */
export async function rederiveMinutes(updates: readonly MinuteRederivation[]): Promise<number> {
  requireTransaction('rederiveMinutes');
  if (updates.length === 0) return 0;
  const table = db().observations;

  for (const update of updates) {
    if (!Number.isInteger(update.matchMinute) || update.matchMinute < 0) {
      throw new StorageError(
        `INV-10: re-derivation produced a matchMinute of ${String(update.matchMinute)} for observation ${update.id}.`,
        { context: { store: TABLE.observations, ...update } },
      );
    }
  }

  let rewritten = 0;
  for (const update of updates) {
    const row = await table.get(update.id);
    if (!row) {
      throw new StorageError(
        `SES-8: observation ${update.id} vanished mid-re-derivation; the adjustment was rolled back.`,
        { context: { store: TABLE.observations, id: update.id } },
      );
    }
    if (row.matchMinute === update.matchMinute) continue;
    await table.put({ ...row, matchMinute: update.matchMinute });
    rewritten += 1;
  }
  return rewritten;
}

/**
 * INV-8's deletion path, and the only other one.
 *
 * Named for its single caller (`repos/cascade.ts`) so a future reader cannot mistake it for a
 * general delete: INV-1 permits a `voiceNote` or `flag` to be removed *only* by cascade deletion of
 * its Match or Team, and that is what this is.
 *
 * @throws {StorageError} outside a transaction, or on a write failure.
 */
export async function deleteObservationsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteObservationsForCascade');
  if (ids.length === 0) return;
  try {
    await db().observations.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the match’s observations during a cascade.', {
      cause,
      context: { store: TABLE.observations, count: ids.length },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LIVE-6's candidate set: this hypothesis's `increment` rows, from the
 * `[matchId+hypothesisId+kind+createdAt]` range with `kind` pinned.
 *
 * The range **structurally cannot** contain another hypothesis's rows or a `voiceNote`/`flag`
 * (design §3.4). `domain/undo.planUndoIncrement` re-filters anyway, so a wrong query here would be a
 * performance bug rather than a correctness one — which is the right way round.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listUndoCandidates(
  matchId: string,
  hypothesisId: string,
): Promise<ObservationRow[]> {
  try {
    return await db()
      .observations.where('[matchId+hypothesisId+kind+createdAt]')
      .between(
        [matchId, hypothesisId, INCREMENT, -Infinity],
        [matchId, hypothesisId, INCREMENT, Infinity],
      )
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the undo candidates.', {
      cause,
      context: { store: TABLE.observations, matchId, hypothesisId },
    });
  }
}

/**
 * INV-2's count: this hypothesis's committed `increment` rows.
 *
 * Used by §13.6's drift detection (run at every state transition, never on the tap path) and by the
 * INV-9 reconciler. Counted over the same pinned range as the undo query, so the two can never
 * disagree about what an "increment on this hypothesis" is.
 *
 * @throws {StorageError} on a read failure.
 */
export async function countIncrements(matchId: string, hypothesisId: string): Promise<number> {
  return (await listUndoCandidates(matchId, hypothesisId)).length;
}

/**
 * Every Observation of a match, in `[matchId+createdAt]` order.
 *
 * The replay/reconcile input (INV-9), the SES-8 re-derivation input, and the sync-staleness input —
 * all three read the same index, so "which rows belong to this match" has one answer.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listObservationsForMatch(matchId: string): Promise<ObservationRow[]> {
  try {
    return await db()
      .observations.where('[matchId+createdAt]')
      .between([matchId, -Infinity], [matchId, Infinity])
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the match’s observations.', {
      cause,
      context: { store: TABLE.observations, matchId },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function getObservation(id: string): Promise<ObservationRow | undefined> {
  try {
    return await db().observations.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the observation.', {
      cause,
      context: { store: TABLE.observations, id },
    });
  }
}
